import { BookUser } from 'lucide-react'
import { Link } from 'react-router'

import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PaginationSettings } from '@/hooks/usePagination'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import { Collection } from '@/interfaces/Collection'
import { Participant } from '@/interfaces/Participant'
import { Role } from '@/types/User'

import { ContactItem } from './ContactItem'
import { Balance } from './contactUtils'

interface ContactsProps {
  contacts: Collection<Participant> | undefined
  isLoading?: boolean
  pagination: PaginationSettings
  balances?: Record<string, Balance>
  balancesLoading?: boolean
  primaryCurrency: string
  /** true = seznam je filtrovaný (hledání, písmeno) */
  filtered?: boolean
  onClearFilter?: () => void
}

export const Contacts = ({
  contacts,
  isLoading,
  pagination,
  balances,
  balancesLoading,
  primaryCurrency,
  filtered,
  onClearFilter,
}: ContactsProps) => {
  const canCreateContact = useRoleCheck([Role.owner, Role.manager, Role.employee])
  const rows = contacts?.data ?? []
  const total = contacts?.total ?? 0
  const from = pagination.offset + 1
  const to = Math.min(pagination.offset + pagination.perPage, total)

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Název</TableHead>
            <TableHead className="hidden w-28 md:table-cell">IČO</TableHead>
            <TableHead className="hidden w-[24%] md:table-cell">E-mail</TableHead>
            <TableHead className="hidden w-36 md:table-cell">Město</TableHead>
            <TableHead className="w-32 text-right md:w-36" title="Neuhrazené faktury vystavené letos a loni">Neuhrazeno</TableHead>
            <TableHead className="w-10 md:w-20" />
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading &&
            Array.from({ length: 10 }).map((_, i) => (
              <TableRow key={i} className="h-11">
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Skeleton className="size-7 rounded-full" />
                    <Skeleton className="h-4 w-24 md:w-40" />
                  </div>
                </TableCell>
                {Array.from({ length: 4 }).map((__, j) => (
                  <TableCell key={j} className={j < 3 ? 'hidden md:table-cell' : undefined}><Skeleton className="h-4 w-full max-w-24" /></TableCell>
                ))}
                <TableCell />
              </TableRow>
            ))}

          {!isLoading &&
            rows.map((contact) => (
              <ContactItem
                key={contact.id}
                contact={contact}
                balance={contact.id ? balances?.[contact.id] : undefined}
                balanceLoading={balancesLoading}
                primaryCurrency={primaryCurrency}
                canEdit={canCreateContact}
              />
            ))}
        </TableBody>
      </Table>

      {!isLoading && rows.length === 0 && (
        <div className="p-6">
          <EmptyState icon={BookUser}
            title={filtered ? 'Žádný kontakt neodpovídá filtru' : 'Zatím tu nejsou žádné kontakty'}
            description={
              filtered
                ? 'Zkuste jiný název, IČO nebo písmeno.'
                : 'Kontakty se ukládají při vystavení dokladu, nebo je přidejte ručně. Údaje doplníme z ARES podle IČO.'
            }
            ctaButton={
              filtered ? (
                <Button variant="outline" onClick={onClearFilter}>Zrušit filtr</Button>
              ) : canCreateContact ? (
                <Button asChild><Link to="/contact/new">Přidat kontakt</Link></Button>
              ) : undefined
            }
          />
        </div>
      )}

      {total > pagination.perPage && (
        <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
          <span className="tabular">
            {from}-{to} z {total}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.currentPage <= 1}
              onClick={() => pagination.setCurrentPage(pagination.currentPage - 1)}
            >
              Předchozí
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.currentPage >= pagination.pageCount}
              onClick={() => pagination.setCurrentPage(pagination.currentPage + 1)}
            >
              Další
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
